// packages/api/src/hooks/pricing/useChangeSetsList.ts
// Phase 08 Plan 06 (ESCR-218, 2026-05-22)

import { useQuery } from '@tanstack/react-query';

import type { ApiSuccess, ListChangeSetsParams, ListChangeSetsResponse } from '@alamo/types';

import { apiClient } from '../../client';
import { pricingChangeSetKeys } from '../../query-keys';

function serializeFilters(f: ListChangeSetsParams): Record<string, unknown> {
  return {
    ...f,
    status: f.status?.join(','),
  };
}

/**
 * Lista Change Sets cross-pilar paginado. BE envelopa em
 * `{ success, data: { count, rows }, timestamp }` — desempacota pra que o
 * caller veja `{ count, rows }` direto.
 *
 * ```tsx
 * const list = useChangeSetsList({ status: ['approval_pending'], limit: 25 });
 * const { count, rows } = list.data ?? { count: 0, rows: [] };
 * ```
 */
export function useChangeSetsList(
  filters: ListChangeSetsParams = {},
  options?: {
    refetchInterval?: number;
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
  },
) {
  return useQuery({
    queryKey: pricingChangeSetKeys.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<ListChangeSetsResponse>>(
        '/admin/pricing/change-sets',
        { params: serializeFilters(filters) },
      );
      return res.data.data;
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
    // `undefined` deixa o default global (refetchOnWindowFocus=false) intacto
    // para os outros callers; o badge da sidebar liga ambos como rede de
    // segurança do push realtime.
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    refetchOnReconnect: options?.refetchOnReconnect,
  });
}
